// src/components/ChatbotLayout.js
import React, { useEffect, useRef } from 'react';
import { Outlet, useParams, useNavigate } from 'react-router-dom';
import { useChatbots } from '../contexts/ChatbotContext';
import { Box, CircularProgress, Typography } from '@mui/material';

export default function ChatbotLayout() {
    const { chatbotId } = useParams(); // This should be chatbot_identity from URL
    const { chatbots, activeChatbot, switchActiveChatbot, isLoading } = useChatbots();
    const navigate = useNavigate();
    const hasProcessedRef = useRef(false); // Prevent multiple processing

    useEffect(() => {
        // Don't do anything until the initial chatbot list has loaded
        if (isLoading) {
            return;
        }

        // Reset the processing flag when chatbotId changes
        if (!hasProcessedRef.current) {
            console.log('ChatbotLayout processing for chatbotId:', chatbotId);
        }

        // Handle case where there are no chatbots at all
        if (chatbots.length === 0) {
            if (!hasProcessedRef.current) {
                hasProcessedRef.current = true;
                navigate('/chatbot/create', { replace: true });
            }
            return;
        }

        // Find the chatbot by chatbot_identity (not by ID)
        const targetBot = chatbots.find(bot => 
            bot.chatbot_identity === chatbotId || 
            bot.id.toString() === chatbotId
        );
        
        if (!targetBot) {
            // If the chatbotId in the URL is invalid, redirect to the default/first bot
            if (!hasProcessedRef.current) {
                hasProcessedRef.current = true;
                const defaultBot = chatbots.find(b => b.is_default) || chatbots[0];
                const currentPath = window.location.pathname;
                const pathParts = currentPath.split('/');
                const remainingPath = pathParts.slice(3).join('/'); // Everything after /chatbot/{id}
                const newPath = `/chatbot/${defaultBot.chatbot_identity}/${remainingPath || 'dashboard'}`;
                
                console.log('Invalid chatbotId, redirecting to:', newPath);
                navigate(newPath, { replace: true });
            }
            return;
        }

        // If the context's active chatbot is not the one in the URL, update it
        if (!activeChatbot || activeChatbot.chatbot_identity !== chatbotId) {
            console.log('Updating active chatbot to:', targetBot.chatbot_name);
            // Update context without navigation
            switchActiveChatbot(targetBot.id, { navigateToDashboard: false });
        }

        hasProcessedRef.current = true;
    }, [chatbotId, chatbots, activeChatbot, isLoading, switchActiveChatbot, navigate]);

    // Reset processing flag when chatbotId changes
    useEffect(() => {
        hasProcessedRef.current = false;
    }, [chatbotId]);
    
    // Show loading if:
    // 1. Context is still loading
    // 2. No active chatbot set yet
    // 3. Active chatbot doesn't match the URL parameter
    if (isLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
                <CircularProgress />
                <Typography sx={{ ml: 2 }}>Loading Chatbots...</Typography>
            </Box>
        );
    }

    if (!activeChatbot) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
                <CircularProgress />
                <Typography sx={{ ml: 2 }}>Initializing Chatbot...</Typography>
            </Box>
        );
    }

    // Check if we have the right chatbot active
    if (activeChatbot.chatbot_identity !== chatbotId && activeChatbot.id.toString() !== chatbotId) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
                <CircularProgress />
                <Typography sx={{ ml: 2 }}>Syncing Chatbot...</Typography>
            </Box>
        );
    }

    // When everything is ready, render the page content
    return <Outlet />;
}